/**
 * Coda di lavorazione (ADR 0043): ogni fonte di dati (casella, caricamento, programma collegato) e i suoi elementi
 * passano dalle stesse fasi; da qui le barre di avanzamento, fonte per fonte e file per file.
 */
export const WORK_SOURCE_KINDS = ['posta', 'caricamento', 'programma', 'documenti'] as const;
export type WorkSourceKind = (typeof WORK_SOURCE_KINDS)[number];

export const WORK_PHASES = ['conteggio', 'scarico', 'archiviazione', 'lettura', 'vettori', 'fatto'] as const;
export type WorkPhase = (typeof WORK_PHASES)[number];

export type WorkStatus = 'da_fare' | 'in_corso' | 'fatto' | 'errore' | 'saltato';

/** Etichette delle fasi per le persone. */
export const WORK_PHASE_LABEL: Record<WorkPhase, string> = {
  conteggio: 'Conto quanti sono',
  scarico: 'In scarico',
  archiviazione: 'In archiviazione',
  lettura: 'In lettura',
  vettori: 'Nell’indice',
  fatto: 'Fatto',
};

export interface WorkSourceDto {
  id: string;
  kind: WorkSourceKind;
  ref: string;
  label: string;
  status: 'in_corso' | 'finito';
  /** Si sta ancora contando quanti elementi ci sono. */
  counting: boolean;
  /** La lettura completa iniziale è finita: si lavorano solo le novità (sincronizzazione automatica). */
  initial_done: boolean;
  /** Elementi principali (messaggi, file, sincronizzazioni), senza i figli (allegati, file negli ZIP). */
  total: number;
  /** Elementi principali arrivati alla fine (fatti, saltati o in errore). */
  done: number;
  /** Allegati e file contenuti: quanti e quanti finiti. */
  children: { total: number; done: number };
  /** Elementi (principali e figli) per fase, solo quelli non finiti. */
  phases: Partial<Record<WorkPhase, number>>;
  errors: number;
  /** Percentuale complessiva (0-100), con i figli. */
  percent: number;
  /** Posta più vecchia che si lavora solo nelle ore della notte: quanti elementi aspettano, da che età, in che ore. */
  night: { items: number; days: number; from: number; to: number; now: boolean } | null;
  /** Perché la fonte è ferma (es. tetto mensile di spesa del motore raggiunto), null se lavora. */
  paused: string | null;
  /** Secondi stimati alla fine dalla velocità reale, null se non si può dire. */
  eta_seconds: number | null;
  /**
   * Cosa sta facendo ora una sincronizzazione di un programma: la scelta dei campi, oppure la lettura in corso con le
   * righe lette fin qui (una sincronizzazione è un solo elemento, che può leggere migliaia di righe). null se non lavora.
   */
  reading: { fase: 'campi' | 'lettura'; risorsa: string | null; righe: number; risorse_lette: number; risorse: number } | null;
  started_at: string;
  finished_at: string | null;
  updated_at: string;
}

export interface WorkItemDto {
  id: string;
  parent_id: string | null;
  kind: 'conteggio' | 'messaggio' | 'file' | 'sincronizzazione';
  label: string;
  size: number | null;
  phase: WorkPhase;
  status: WorkStatus;
  error: string | null;
  note: string | null;
  attempts: number;
  document_id: string | null;
  /** Avanzamento interno (es. righe lette di una sincronizzazione: { righe, risorsa }). */
  progress: Record<string, unknown>;
  updated_at: string;
}

export interface WorkOverviewDto {
  sources: WorkSourceDto[];
  /** Somma delle fonti ancora al lavoro. */
  active: { sources: number; items: number; done: number; percent: number; eta_seconds: number | null };
}

export interface WorkItemsDto {
  source: WorkSourceDto;
  items: WorkItemDto[];
  /** Elementi in tutto con quel filtro (la pagina ne mostra una parte). */
  total: number;
}
